import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Check, CreditCard, Shield, Cloud, Server, Loader2, AlertCircle, CheckCircle, ShoppingCart, X, Plus, Usb, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentPage = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedInterval, setSelectedInterval] = useState('annual');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Bundle checkout state
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [showBundleModal, setShowBundleModal] = useState(false);
  
  // Check if returning from Stripe
  const sessionId = searchParams.get('session_id');
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    fetchPackages();
    
    // If returning from Stripe, check payment status
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const fetchPackages = async () => {
    try {
      const response = await fetch(`${API_URL}/api/payments/packages`);
      if (response.ok) {
        const data = await response.json();
        setPackages(data.packages);
      }
    } catch (err) {
      console.error('Failed to fetch packages:', err);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus({ status: 'timeout', message: 'Payment status check timed out. Please check your email for confirmation.' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/payments/status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to check payment status');
      }

      const data = await response.json();

      if (data.payment_status === 'paid') {
        setPaymentStatus({ 
          status: 'success', 
          message: data.message,
          packageName: data.package_name,
          amount: data.amount
        });
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus({ status: 'expired', message: 'Payment session expired. Please try again.' });
        return;
      }

      // Continue polling
      setPaymentStatus({ status: 'processing', message: 'Payment is being processed...' });
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      setPaymentStatus({ status: 'error', message: 'Error checking payment status.' });
    }
  };

  // Insurance packages require bundling with a subscription
  const insurancePackageIds = ['device_insurance_1yr', 'device_insurance_2yr'];
  const subscriptionPackageIds = ['basic_monthly', 'basic_annual', 'professional_monthly', 
                                   'professional_annual', 'enterprise_monthly', 'enterprise_annual',
                                   'standalone_license'];

  const handleSelectPlan = (packageId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login?redirect=/pricing');
      return;
    }
    
    setSelectedPlan(packageId);
    setSelectedAddons([]);
    setShowBundleModal(true);
  };

  const toggleAddon = (addonId) => {
    setSelectedAddons(prev => {
      if (prev.includes(addonId)) {
        return prev.filter(id => id !== addonId);
      } else {
        // For insurance, only allow one type
        if (insurancePackageIds.includes(addonId)) {
          const otherInsurance = prev.filter(id => !insurancePackageIds.includes(id));
          return [...otherInsurance, addonId];
        }
        return [...prev, addonId];
      }
    });
  };

  const calculateBundleTotal = () => {
    if (!selectedPlan) return 0;
    const mainPackage = packages.find(p => p.id === selectedPlan);
    let total = mainPackage?.amount || 0;
    
    selectedAddons.forEach(addonId => {
      const addon = packages.find(p => p.id === addonId);
      if (addon) total += addon.amount;
    });
    
    return total;
  };

  const handleBundleCheckout = async () => {
    setProcessing(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const originUrl = window.location.origin;
      
      let response;
      
      if (selectedAddons.length > 0) {
        // Use bundle checkout
        response = await fetch(
          `${API_URL}/api/payments/checkout-bundle?main_package_id=${selectedPlan}&origin_url=${encodeURIComponent(originUrl)}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(selectedAddons)
          }
        );
      } else {
        // Single package checkout
        response = await fetch(
          `${API_URL}/api/payments/checkout?package_id=${selectedPlan}&origin_url=${encodeURIComponent(originUrl)}`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const data = await response.json();
      
      // Redirect to Stripe Checkout
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      setError(err.message);
      setProcessing(false);
    }
  };

  const tierIcons = {
    basic: <Shield className="w-8 h-8" />,
    professional: <Cloud className="w-8 h-8" />,
    enterprise: <Server className="w-8 h-8" />,
    standalone: <Shield className="w-8 h-8" />,
    addon: <CreditCard className="w-8 h-8" />
  };

  const tierColors = {
    basic: 'from-blue-500 to-blue-600',
    professional: 'from-purple-500 to-purple-600',
    enterprise: 'from-orange-500 to-orange-600',
    standalone: 'from-emerald-500 to-emerald-600',
    addon: 'from-green-500 to-green-600'
  };

  const tierFeatures = {
    basic: [
      'USB/Offline Mode',
      'Local Encrypted Database',
      'Single Workstation',
      'FD-258 Card Generation',
      'Email Support (48hr)'
    ],
    professional: [
      'Everything in Basic',
      'Cloud Storage & Sync',
      'Multi-device Access (up to 5)',
      'Real-time Backup',
      'Priority Support (24hr)',
      'Additional Scanner Licenses Available'
    ],
    enterprise: [
      'Everything in Professional',
      'LAN Installation',
      'Unlimited Workstations',
      'Custom Branding',
      'Dedicated Support (4hr)',
      'SLA Guarantee',
      'On-site Training'
    ],
    standalone: [
      'LIFETIME License - No Monthly Fees',
      '5 Years Security & Bug Fix Updates',
      'FD-258 & FD-1164 Card Generation',
      'Free Remote Setup ($249 value)',
      'Free Training Session ($89 value)',
      'Priority Support (24hr)',
      'USB/Offline Mode',
      'Local Encrypted Database'
    ]
  };

  const addonFeatures = {
    'usb_device': [
      'Pre-loaded FingerprintVS Software',
      'Hardware USB Drive Included',
      'Auto-run Configuration',
      'Required for offline use'
    ],
    'device_insurance_1yr': [
      'Accidental Damage Coverage',
      'Theft Protection',
      '$20 Deductible Replacement',
      '1 Year Coverage'
    ],
    'device_insurance_2yr': [
      'Accidental Damage Coverage',
      'Theft Protection',
      '$20 Deductible Replacement',
      '2 Year Coverage (Best Value)'
    ],
    'setup_remote_basic': [
      'For Basic Tier Customers',
      '30-45 Minute Remote Session',
      'Screen Share Installation',
      'Live Technician Support'
    ],
    'setup_remote_professional': [
      'For Professional Tier Customers',
      '45-60 Minute Remote Session',
      'Multi-device Setup',
      'Cloud Sync Configuration'
    ],
    'setup_enterprise_package': [
      'For Enterprise Tier Customers',
      'Covers Up to 25 Installations',
      'Current & Future Installs Included',
      'LAN/On-Premise Configuration'
    ],
    'training_session': [
      '1 Hour Training',
      'All Features Covered',
      'Q&A Session'
    ],
    'white_label': [
      'Custom Branding',
      'Your Logo',
      'Custom Colors'
    ],
    'scanner_license_1_4': [
      'For Professional & Enterprise tiers',
      'Add 1-4 additional scanners',
      '$99 per scanner license'
    ],
    'scanner_license_5_19': [
      'For Professional & Enterprise tiers',
      'Add 5-19 additional scanners',
      '$75 per scanner license (volume discount)'
    ],
    'scanner_license_20_plus': [
      'For Professional & Enterprise tiers',
      'Add 20+ additional scanners',
      '$49 per scanner license (best rate)'
    ],
    'bundle_basic_starter': [
      'USB Device ($199 value)',
      'Basic Monthly Subscription ($49)',
      'Remote Setup Session ($149 value)',
      'Save $0 - Convenience Bundle'
    ],
    'bundle_pro_complete': [
      'USB Device ($199 value)',
      'Professional Annual ($999)',
      'Remote Setup Session ($249 value)',
      '1 Year Insurance ($99 value)',
      'Save $0 - Complete Package'
    ],
    'bundle_standalone_complete': [
      'USB Device ($199 value)',
      'Lifetime License ($4,999)',
      '1 Year Insurance ($99 value)',
      'Save $0 - Best for High Volume'
    ]
  };

  // Payment success/status display
  if (paymentStatus) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-2xl p-8 max-w-md w-full text-center">
          {paymentStatus.status === 'success' && (
            <>
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Payment Successful!</h2>
              <p className="text-gray-400 mb-4">{paymentStatus.message}</p>
              {paymentStatus.packageName && (
                <p className="text-green-400 font-semibold mb-6">
                  {paymentStatus.packageName} - ${paymentStatus.amount}
                </p>
              )}
              <button
                onClick={() => navigate('/dashboard')}
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Go to Dashboard
              </button>
            </>
          )}
          
          {paymentStatus.status === 'processing' && (
            <>
              <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Processing Payment</h2>
              <p className="text-gray-400">{paymentStatus.message}</p>
            </>
          )}
          
          {(paymentStatus.status === 'expired' || paymentStatus.status === 'error' || paymentStatus.status === 'timeout') && (
            <>
              <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Payment Issue</h2>
              <p className="text-gray-400 mb-6">{paymentStatus.message}</p>
              <button
                onClick={() => {
                  setPaymentStatus(null);
                  navigate('/pricing');
                }}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
              >
                Try Again
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center relative">
        <div 
          className="fixed inset-0 z-0"
          style={{
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/07e51985-c374-4472-a4ec-7832a182bb43/images/a599bfb20d8b730453411fb0496e41df29ac3936a599b1146f16c28f94161081.png')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="fixed inset-0 z-0 bg-slate-900/90" />
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin relative z-10" />
      </div>
    );
  }

  // Filter packages by interval
  const filteredPackages = packages.filter(pkg => {
    if (pkg.tier === 'standalone') return true; // Always show standalone
    if (pkg.interval === 'one-time' && pkg.tier === 'addon') return true;
    return selectedInterval === 'monthly' ? pkg.interval === 'month' : pkg.interval === 'year';
  });

  // Group by tier
  const subscriptionPackages = filteredPackages.filter(pkg => 
    subscriptionPackageIds.includes(pkg.id) && pkg.tier !== 'standalone'
  );
  const standalonePackage = filteredPackages.find(pkg => pkg.id === 'standalone_license');
  const addonPackages = filteredPackages.filter(pkg => pkg.tier === 'addon');
  const nonInsuranceAddons = addonPackages.filter(pkg => !insurancePackageIds.includes(pkg.id));

  return (
    <div className="min-h-screen bg-slate-900 py-12 px-4 relative" data-testid="pricing-page">
      {/* Main Background Image */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/07e51985-c374-4472-a4ec-7832a182bb43/images/a599bfb20d8b730453411fb0496e41df29ac3936a599b1146f16c28f94161081.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      />
      {/* Dark overlay */}
      <div className="fixed inset-0 z-0 bg-slate-900/92" />
      
      {/* Content */}
      <div className="relative z-10">
      {/* Header */}
      <div className="max-w-6xl mx-auto text-center mb-12">
        <h1 className="text-4xl font-bold text-white mb-4">
          Choose Your Plan
        </h1>
        <p className="text-gray-400 text-lg mb-8">
          Select the perfect plan for your fingerprint verification needs
        </p>

        {/* Interval Toggle */}
        <div className="inline-flex bg-gray-800 rounded-lg p-1">
          <button
            onClick={() => setSelectedInterval('monthly')}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              selectedInterval === 'monthly'
                ? 'bg-blue-500 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
            data-testid="monthly-toggle"
          >
            Monthly
          </button>
          <button
            onClick={() => setSelectedInterval('annual')}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              selectedInterval === 'annual'
                ? 'bg-blue-500 text-white'
                : 'text-gray-400 hover:text-white'
            }`}
            data-testid="annual-toggle"
          >
            Annual <span className="text-green-400 text-sm">(Save 15%)</span>
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="max-w-6xl mx-auto mb-8">
          <div className="bg-red-500/10 border border-red-500 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-red-400">{error}</p>
          </div>
        </div>
      )}

      {/* Subscription Plans */}
      <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8 mb-12">
        {subscriptionPackages.map((pkg) => (
          <div
            key={pkg.id}
            className={`bg-gray-800 rounded-2xl p-6 border-2 transition-all hover:scale-105 ${
              pkg.tier === 'professional' ? 'border-purple-500 ring-2 ring-purple-500/20' : 'border-gray-700'
            }`}
            data-testid={`plan-card-${pkg.tier}`}
          >
            {pkg.tier === 'professional' && (
              <div className="bg-purple-500 text-white text-sm font-semibold px-3 py-1 rounded-full inline-block mb-4">
                MOST POPULAR
              </div>
            )}
            
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${tierColors[pkg.tier]} flex items-center justify-center text-white mb-4`}>
              {tierIcons[pkg.tier]}
            </div>
            
            <h3 className="text-xl font-bold text-white mb-2">
              {pkg.tier.charAt(0).toUpperCase() + pkg.tier.slice(1)}
            </h3>
            
            <div className="mb-6">
              <span className="text-4xl font-bold text-white">${pkg.amount}</span>
              <span className="text-gray-400">/{pkg.interval === 'year' ? 'year' : 'month'}</span>
            </div>

            <ul className="space-y-3 mb-6">
              {tierFeatures[pkg.tier]?.map((feature, idx) => (
                <li key={idx} className="flex items-center gap-2 text-gray-300">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => handleSelectPlan(pkg.id)}
              disabled={processing}
              className={`w-full py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                pkg.tier === 'professional'
                  ? 'bg-purple-500 hover:bg-purple-600 text-white'
                  : 'bg-gray-700 hover:bg-gray-600 text-white'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid={`subscribe-btn-${pkg.tier}`}
            >
              <ShoppingCart className="w-5 h-5" />
              Select Plan
            </button>
          </div>
        ))}
      </div>

      {/* Standalone License */}
      {standalonePackage && (
        <div className="max-w-2xl mx-auto mb-12">
          <div className="bg-gradient-to-br from-emerald-900/50 to-emerald-800/30 rounded-2xl p-8 border-2 border-emerald-500 relative overflow-hidden">
            {/* Best Value Badge */}
            <div className="absolute top-4 right-4">
              <div className="bg-emerald-500 text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg">
                BEST VALUE - LIFETIME
              </div>
            </div>
            
            <div className="flex items-center gap-4 mb-4">
              <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${tierColors.standalone} flex items-center justify-center text-white`}>
                {tierIcons.standalone}
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white">Standalone License</h3>
                <p className="text-emerald-400">One-Time Purchase - Own It Forever</p>
              </div>
            </div>
            
            <div className="mb-4">
              <span className="text-5xl font-bold text-white">${standalonePackage.amount.toLocaleString()}</span>
              <span className="text-gray-400 ml-2">one-time payment</span>
            </div>
            
            {/* Value Comparison */}
            <div className="bg-emerald-500/20 rounded-lg p-4 mb-6">
              <p className="text-emerald-300 text-sm font-medium mb-2">Why Standalone is the Smart Choice:</p>
              <ul className="text-emerald-200 text-sm space-y-1">
                <li>✓ <strong>Includes $338 in extras:</strong> Remote Setup + Training Session</li>
                <li>✓ <strong>No recurring fees</strong> - pay once, use forever</li>
                <li>✓ <strong>5 years of security & bug fix updates</strong> included</li>
                <li>✓ <strong>Use our Profit Calculator</strong> to compare long-term costs</li>
              </ul>
            </div>

            <ul className="grid md:grid-cols-2 gap-3 mb-6">
              {tierFeatures.standalone?.map((feature, idx) => (
                <li key={idx} className="flex items-center gap-2 text-gray-300">
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => handleSelectPlan(standalonePackage.id)}
              disabled={processing}
              className="w-full py-4 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
              data-testid="subscribe-btn-standalone"
            >
              <ShoppingCart className="w-5 h-5" />
              Purchase Lifetime License
            </button>
            
            <p className="text-emerald-300/70 text-xs text-center mt-3">
              Ideal for high-volume users and businesses that prefer ownership over subscription
            </p>
          </div>
        </div>
      )}

      {/* Add-ons Section (excluding insurance - shown in modal) */}
      {nonInsuranceAddons.length > 0 && (
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">Add-ons & Services</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {nonInsuranceAddons.map((pkg) => (
              <div
                key={pkg.id}
                className="bg-gray-800 rounded-xl p-6 border border-gray-700"
                data-testid={`addon-card-${pkg.id}`}
              >
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${tierColors.addon} flex items-center justify-center text-white mb-4`}>
                  {tierIcons.addon}
                </div>
                
                <h3 className="text-lg font-bold text-white mb-2">{pkg.name}</h3>
                
                <div className="mb-4">
                  <span className="text-2xl font-bold text-white">${pkg.amount}</span>
                  <span className="text-gray-400"> {pkg.interval === 'month' ? '/month' : 'one-time'}</span>
                </div>

                {addonFeatures[pkg.id] && (
                  <ul className="space-y-2 mb-4">
                    {addonFeatures[pkg.id].map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-gray-300 text-sm">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <p className="text-gray-500 text-sm italic">
                  Add during plan selection
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Compare Plans Table */}
      <div className="max-w-6xl mx-auto mt-16" data-testid="compare-plans-section">
        <h2 className="text-3xl font-bold text-white text-center mb-8">Compare Plans</h2>
        <div className="overflow-x-auto">
          <table className="w-full bg-gray-800 rounded-xl overflow-hidden">
            <thead>
              <tr className="bg-gray-700">
                <th className="text-left text-white font-semibold p-4 border-b border-gray-600">Feature</th>
                <th className="text-center text-blue-400 font-semibold p-4 border-b border-gray-600">Basic<br/><span className="text-xs text-gray-400">$49/mo</span></th>
                <th className="text-center text-purple-400 font-semibold p-4 border-b border-gray-600">Professional<br/><span className="text-xs text-gray-400">$99/mo</span></th>
                <th className="text-center text-orange-400 font-semibold p-4 border-b border-gray-600">Enterprise<br/><span className="text-xs text-gray-400">$399/mo</span></th>
                <th className="text-center text-emerald-400 font-semibold p-4 border-b border-gray-600 relative">
                  Standalone<br/><span className="text-xs text-gray-400">$4,999 one-time</span>
                  <span className="absolute -top-2 right-2 bg-emerald-500 text-white text-[10px] px-2 py-0.5 rounded-full">LIFETIME</span>
                </th>
              </tr>
            </thead>
            <tbody className="text-gray-300">
              {/* License Type */}
              <tr className="bg-gray-750">
                <td colSpan="5" className="text-white font-semibold p-3 bg-gray-700/50">License & Billing</td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">License Type</td>
                <td className="text-center p-4 text-white">Monthly/Annual</td>
                <td className="text-center p-4 text-white">Monthly/Annual</td>
                <td className="text-center p-4 text-white">Monthly/Annual</td>
                <td className="text-center p-4 text-emerald-400 font-semibold">LIFETIME</td>
              </tr>
              
              {/* Capture Features */}
              <tr className="bg-gray-750">
                <td colSpan="5" className="text-white font-semibold p-3 bg-gray-700/50">Fingerprint Capture</td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">FBI-Compliant Capture (500 DPI)</td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">4-Finger Slap + Rolled Capture</td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">ID Verification with OCR</td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
              </tr>
              
              {/* Card Generation */}
              <tr className="bg-gray-750">
                <td colSpan="5" className="text-white font-semibold p-3 bg-gray-700/50">Card Generation</td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">FD-258 Civil Cards</td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">FD-1164 Criminal Cards</td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-emerald-500 mx-auto" /><span className="text-emerald-400 text-xs block">Included</span></td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">EBTS Export (Electronic Submission)</td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /><span className="text-yellow-400 text-xs block">*FBI Cert Req.</span></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /><span className="text-yellow-400 text-xs block">*FBI Cert Req.</span></td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">WSQ Compression</td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /><span className="text-yellow-400 text-xs block">*FBI Cert Req.</span></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /><span className="text-yellow-400 text-xs block">*FBI Cert Req.</span></td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
              </tr>
              
              {/* Deployment */}
              <tr className="bg-gray-750">
                <td colSpan="5" className="text-white font-semibold p-3 bg-gray-700/50">Deployment Options</td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">USB/Offline Mode</td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">Cloud Access & Sync</td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">LAN/On-Premise Installation</td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
              </tr>
              
              {/* Users & Capacity */}
              <tr className="bg-gray-750">
                <td colSpan="5" className="text-white font-semibold p-3 bg-gray-700/50">Users & Capacity</td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">User Accounts</td>
                <td className="text-center p-4 text-white">1</td>
                <td className="text-center p-4 text-white">5</td>
                <td className="text-center p-4 text-white">Unlimited</td>
                <td className="text-center p-4 text-white">1</td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">Workstations</td>
                <td className="text-center p-4 text-white">1</td>
                <td className="text-center p-4 text-white">5</td>
                <td className="text-center p-4 text-white">Unlimited</td>
                <td className="text-center p-4 text-white">1</td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">Clients/Month</td>
                <td className="text-center p-4 text-white">Unlimited</td>
                <td className="text-center p-4 text-white">Unlimited</td>
                <td className="text-center p-4 text-white">Unlimited</td>
                <td className="text-center p-4 text-white">Unlimited</td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">Additional Scanner Licenses</td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /><span className="text-purple-400 text-xs block">Available</span></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /><span className="text-orange-400 text-xs block">Available</span></td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
              </tr>
              
              {/* Security */}
              <tr className="bg-gray-750">
                <td colSpan="5" className="text-white font-semibold p-3 bg-gray-700/50">Security & Compliance</td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">AES-256 Encryption</td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">Multi-Factor Authentication (MFA)</td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">Audit Logging</td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">CJIS Security Policy Compliant</td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
              </tr>
              
              {/* Support & Included Services */}
              <tr className="bg-gray-750">
                <td colSpan="5" className="text-white font-semibold p-3 bg-gray-700/50">Support & Included Services</td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">Email Support Response</td>
                <td className="text-center p-4 text-white">48hr</td>
                <td className="text-center p-4 text-white">24hr</td>
                <td className="text-center p-4 text-white">4hr</td>
                <td className="text-center p-4 text-emerald-400 font-medium">24hr</td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">Phone Support</td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">Remote Setup Session</td>
                <td className="text-center p-4 text-white text-sm">$149 add-on</td>
                <td className="text-center p-4 text-white text-sm">$249 add-on</td>
                <td className="text-center p-4 text-white text-sm">$499 (up to 25)</td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-emerald-500 mx-auto" /><span className="text-emerald-400 text-xs block">Included</span></td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">Training Session ($89 value)</td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-emerald-500 mx-auto" /><span className="text-emerald-400 text-xs block">Included</span></td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">White-Label Branding</td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">API Access</td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /></td>
                <td className="text-center p-4"><X className="w-5 h-5 text-gray-500 mx-auto" /></td>
              </tr>
              <tr className="border-b border-gray-700 hover:bg-gray-700/30">
                <td className="p-4">Security & Bug Fix Updates</td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /><span className="text-gray-400 text-xs block">Included</span></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /><span className="text-gray-400 text-xs block">Included</span></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /><span className="text-gray-400 text-xs block">Included</span></td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-emerald-500 mx-auto" /><span className="text-emerald-400 text-xs block">5 Years</span></td>
              </tr>
              <tr className="hover:bg-gray-700/30">
                <td className="p-4">New Feature Updates</td>
                <td className="text-center p-4 text-white text-sm">Additional Fee</td>
                <td className="text-center p-4 text-white text-sm">Additional Fee</td>
                <td className="text-center p-4"><Check className="w-5 h-5 text-green-500 mx-auto" /><span className="text-orange-400 text-xs block">Included</span></td>
                <td className="text-center p-4 text-white text-sm">Additional Fee</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Value Bundles Section */}
        <div className="mt-12" data-testid="bundles-section">
          <h3 className="text-2xl font-bold text-white text-center mb-2">Value Bundles</h3>
          <p className="text-gray-400 text-center mb-8">Get everything you need in one convenient package</p>
          
          <div className="grid md:grid-cols-3 gap-6">
            {/* Basic Starter Bundle */}
            <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 rounded-xl p-6 border border-blue-500/30 hover:border-blue-500/60 transition-all">
              <div className="text-blue-400 text-sm font-semibold mb-2">STARTER</div>
              <h4 className="text-xl font-bold text-white mb-1">Basic Starter Bundle</h4>
              <div className="mb-4">
                <span className="text-3xl font-bold text-white">$397</span>
                <span className="text-gray-400 ml-2">one-time + $49/mo</span>
              </div>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-gray-300 text-sm">
                  <Check className="w-4 h-4 text-blue-400" />
                  USB Device ($199 value)
                </li>
                <li className="flex items-center gap-2 text-gray-300 text-sm">
                  <Check className="w-4 h-4 text-blue-400" />
                  Basic Monthly Subscription
                </li>
                <li className="flex items-center gap-2 text-gray-300 text-sm">
                  <Check className="w-4 h-4 text-blue-400" />
                  Remote Setup Session ($149 value)
                </li>
              </ul>
              <button
                onClick={() => handleSelectPlan('bundle_basic_starter')}
                disabled={processing}
                className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-semibold transition-colors"
                data-testid="bundle-basic-btn"
              >
                Get Started
              </button>
            </div>

            {/* Professional Complete Bundle */}
            <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 rounded-xl p-6 border-2 border-purple-500 relative hover:border-purple-400 transition-all">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                MOST POPULAR
              </div>
              <div className="text-purple-400 text-sm font-semibold mb-2">PROFESSIONAL</div>
              <h4 className="text-xl font-bold text-white mb-1">Pro Complete Bundle</h4>
              <div className="mb-4">
                <span className="text-3xl font-bold text-white">$1,546</span>
                <span className="text-gray-400 ml-2">first year</span>
              </div>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-gray-300 text-sm">
                  <Check className="w-4 h-4 text-purple-400" />
                  USB Device ($199 value)
                </li>
                <li className="flex items-center gap-2 text-gray-300 text-sm">
                  <Check className="w-4 h-4 text-purple-400" />
                  Professional Annual ($999)
                </li>
                <li className="flex items-center gap-2 text-gray-300 text-sm">
                  <Check className="w-4 h-4 text-purple-400" />
                  Remote Setup Session ($249 value)
                </li>
                <li className="flex items-center gap-2 text-gray-300 text-sm">
                  <Check className="w-4 h-4 text-purple-400" />
                  1 Year Device Insurance ($99 value)
                </li>
              </ul>
              <button
                onClick={() => handleSelectPlan('bundle_pro_complete')}
                disabled={processing}
                className="w-full py-3 bg-purple-600 hover:bg-purple-500 text-white rounded-lg font-semibold transition-colors"
                data-testid="bundle-pro-btn"
              >
                Get Pro Bundle
              </button>
            </div>

            {/* Standalone Complete Bundle */}
            <div className="bg-gradient-to-br from-emerald-900/30 to-emerald-800/20 rounded-xl p-6 border border-emerald-500/30 hover:border-emerald-500/60 transition-all">
              <div className="text-emerald-400 text-sm font-semibold mb-2">LIFETIME</div>
              <h4 className="text-xl font-bold text-white mb-1">Standalone Complete</h4>
              <div className="mb-4">
                <span className="text-3xl font-bold text-white">$5,297</span>
                <span className="text-gray-400 ml-2">one-time</span>
              </div>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-gray-300 text-sm">
                  <Check className="w-4 h-4 text-emerald-400" />
                  USB Device ($199 value)
                </li>
                <li className="flex items-center gap-2 text-gray-300 text-sm">
                  <Check className="w-4 h-4 text-emerald-400" />
                  Lifetime License ($4,999)
                </li>
                <li className="flex items-center gap-2 text-gray-300 text-sm">
                  <Check className="w-4 h-4 text-emerald-400" />
                  1 Year Device Insurance ($99)
                </li>
                <li className="flex items-center gap-2 text-gray-300 text-sm">
                  <Check className="w-4 h-4 text-emerald-400" />
                  Remote Setup Included
                </li>
                <li className="flex items-center gap-2 text-gray-300 text-sm">
                  <Check className="w-4 h-4 text-emerald-400" />
                  Training Session Included
                </li>
              </ul>
              <button
                onClick={() => handleSelectPlan('bundle_standalone_complete')}
                disabled={processing}
                className="w-full py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg font-semibold transition-colors"
                data-testid="bundle-standalone-btn"
              >
                Get Lifetime Bundle
              </button>
            </div>
          </div>
        </div>
        
        {/* Supported Scanners */}
        <div className="mt-12 bg-gray-800 rounded-xl p-6">
          <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Server className="w-6 h-6 text-blue-500" />
            Supported Scanners
          </h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <Check className="w-5 h-5 text-green-500" />
                <span className="text-white font-semibold">Suprema RealScan G10</span>
              </div>
              <p className="text-gray-400 text-sm">FBI PIV/FAP60 certified, 500 DPI, USB 2.0</p>
              <span className="inline-block mt-2 px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">Fully Supported</span>
            </div>
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="flex items-center gap-3 mb-2">
                <Check className="w-5 h-5 text-yellow-500" />
                <span className="text-white font-semibold">Suprema RealScan G1</span>
              </div>
              <p className="text-gray-400 text-sm">Entry-level scanner, USB connectivity</p>
              <span className="inline-block mt-2 px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs rounded">Compatible</span>
            </div>
          </div>
          <p className="text-gray-500 text-sm mt-4">
            Additional scanner support planned: Crossmatch Guardian, Digital Persona U.are.U 4500, Integrated Biometrics Watson Mini
          </p>
        </div>

        {/* Important Notices Section */}
        <div className="mt-12 space-y-4">
          <h3 className="text-xl font-bold text-white mb-4">Important Information</h3>
          
          {/* USB Device Notice */}
          <div className="bg-slate-800/90 backdrop-blur-sm border border-cyan-500/50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Usb className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-cyan-300 font-semibold mb-1">USB Device Required ($199)</h4>
                <p className="text-gray-300 text-sm">
                  All plans require a USB device pre-loaded with FingerprintVS software. This is a one-time hardware fee. 
                  The device enables offline operation and secure local data storage. Check out our <strong className="text-white">Value Bundles</strong> above for convenience.
                </p>
              </div>
            </div>
          </div>

          {/* Device Insurance Notice */}
          <div className="bg-slate-800/90 backdrop-blur-sm border border-amber-500/50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Shield className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-amber-300 font-semibold mb-1">Device Insurance (Add-On Only)</h4>
                <p className="text-gray-300 text-sm">
                  Device insurance ($99/1yr or $199/2yr) is available as an <strong className="text-white">add-on</strong> when purchasing any subscription or standalone license. 
                  Coverage includes replacement with only a $20 deductible for lost, stolen, or damaged devices.
                </p>
              </div>
            </div>
          </div>

          {/* Scanner License Notice */}
          <div className="bg-slate-800/90 backdrop-blur-sm border border-purple-500/50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Server className="w-6 h-6 text-purple-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-purple-300 font-semibold mb-1">Additional Scanner Licenses (Pro/Enterprise Only)</h4>
                <p className="text-gray-300 text-sm">
                  Need more scanners? Additional scanner licenses are available for <strong className="text-white">Professional and Enterprise</strong> tiers only. 
                  Volume discounts apply: $99 each (1-4), $75 each (5-19), $49 each (20+).
                </p>
              </div>
            </div>
          </div>

          {/* FBI Channeler Notice */}
          <div className="bg-slate-800/90 backdrop-blur-sm border border-blue-500/50 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <FileText className="w-6 h-6 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-blue-300 font-semibold mb-1">EBTS/WSQ Features - FBI Channeler Certification Required</h4>
                <p className="text-gray-300 text-sm mb-2">
                  Electronic submission (EBTS) and WSQ compression features require <strong className="text-white">FBI Channeler certification</strong>. 
                  This is a rigorous approval process through the FBI CJIS Division.
                </p>
                <p className="text-gray-300 text-sm mb-2">
                  <strong className="text-white">Already a certified channeler?</strong> Contact us to enable EBTS/WSQ features on your account.
                </p>
                <p className="text-gray-300 text-sm">
                  <strong className="text-white">Not certified?</strong> You can still submit physical FD-258 cards through traditional mail, 
                  or partner with an existing FBI Channeler service. <em className="text-blue-300">Future feature: Direct channeler submission integration.</em>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-12 text-center">
          <p className="text-gray-500 mb-4">Secure payments powered by</p>
          <div className="flex items-center justify-center gap-6">
            <div className="bg-gray-800 px-4 py-2 rounded-lg">
              <span className="text-white font-semibold">Stripe</span>
            </div>
            <div className="flex items-center gap-2 text-gray-400">
              <Shield className="w-5 h-5" />
              <span>256-bit SSL</span>
            </div>
            <div className="flex items-center gap-2 text-gray-400">
              <Check className="w-5 h-5" />
              <span>PCI Compliant</span>
            </div>
          </div>
        </div>

      </div>
      </div>

      {/* Bundle Checkout Modal */}
      {showBundleModal && selectedPlan && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-gray-800 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Complete Your Order</h3>
              <button 
                onClick={() => setShowBundleModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Selected Plan */}
            <div className="bg-gray-700 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-semibold">
                    {packages.find(p => p.id === selectedPlan)?.name}
                  </p>
                  <p className="text-gray-400 text-sm">Selected Plan</p>
                </div>
                <p className="text-xl font-bold text-white">
                  ${packages.find(p => p.id === selectedPlan)?.amount}
                </p>
              </div>
            </div>

            {/* Insurance Add-ons (Required to bundle with device) */}
            <div className="mb-6">
              <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-500" />
                Device Insurance (Optional)
              </h4>
              <p className="text-gray-400 text-sm mb-3">
                Protect your scanner with replacement coverage. Only $20 deductible for claims.
              </p>
              
              {addonPackages.filter(pkg => insurancePackageIds.includes(pkg.id)).map(pkg => (
                <label 
                  key={pkg.id}
                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer mb-2 transition-colors ${
                    selectedAddons.includes(pkg.id) 
                      ? 'bg-amber-500/20 border border-amber-500' 
                      : 'bg-gray-700 border border-transparent hover:bg-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedAddons.includes(pkg.id)}
                      onChange={() => toggleAddon(pkg.id)}
                      className="w-5 h-5 rounded border-gray-500 text-amber-500 focus:ring-amber-500"
                    />
                    <div>
                      <p className="text-white font-medium">{pkg.name}</p>
                      <ul className="text-gray-400 text-xs mt-1">
                        {addonFeatures[pkg.id]?.slice(0, 2).map((f, i) => (
                          <li key={i}>• {f}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <span className="text-white font-semibold">+${pkg.amount}</span>
                </label>
              ))}
            </div>

            {/* Other Add-ons */}
            <div className="mb-6">
              <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                <Plus className="w-5 h-5 text-blue-500" />
                Additional Services (Optional)
              </h4>
              
              {addonPackages.filter(pkg => !insurancePackageIds.includes(pkg.id)).map(pkg => (
                <label 
                  key={pkg.id}
                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer mb-2 transition-colors ${
                    selectedAddons.includes(pkg.id) 
                      ? 'bg-blue-500/20 border border-blue-500' 
                      : 'bg-gray-700 border border-transparent hover:bg-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedAddons.includes(pkg.id)}
                      onChange={() => toggleAddon(pkg.id)}
                      className="w-5 h-5 rounded border-gray-500 text-blue-500 focus:ring-blue-500"
                    />
                    <div>
                      <p className="text-white font-medium">{pkg.name}</p>
                    </div>
                  </div>
                  <span className="text-white font-semibold">+${pkg.amount}</span>
                </label>
              ))}
            </div>

            {/* Order Summary */}
            <div className="border-t border-gray-700 pt-4 mb-6">
              <div className="flex items-center justify-between text-lg">
                <span className="text-gray-300">Total</span>
                <span className="text-2xl font-bold text-white">${calculateBundleTotal().toLocaleString()}</span>
              </div>
            </div>

            {/* Checkout Button */}
            <button
              onClick={handleBundleCheckout}
              disabled={processing}
              className="w-full py-4 bg-green-500 hover:bg-green-600 text-white rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="bundle-checkout-btn"
            >
              {processing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  Proceed to Checkout
                </>
              )}
            </button>

            <p className="text-gray-500 text-xs text-center mt-4">
              You'll be redirected to Stripe for secure payment processing
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentPage;
